import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { StatisticFilterComponent } from '.'
import { NumberFormat, TableComponent } from '../../../../components'
import { translate } from '../../../../languages'
import { TankStatisticEntity } from '../../../../models'
import { RootState } from '../../../../stores'
import {
  loadTankStatistic,
  setTankStatisticFilter,
  setTankStatisticLoaded,
} from '../../../../stores/actions'

const TankStatisticContainer = styled.div<{
  style: {
    isDesktopScreen: boolean
  }
}>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-top: ${(props) => (props.style.isDesktopScreen ? 100 : -15)}px;
  padding-bottom: 75px;
`

const TankStatisticTitle = styled.div`
  width: 100%;
  font-size: 1.6rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
`

export const TankStatisticComponent: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { tankStatistic, isTankStatisticLoaded, tankStatisticFilter } =
    useSelector((state: RootState) => state.tankStatistic)

  const [filter, setFilter] = React.useState(tankStatisticFilter)

  React.useEffect(() => {
    const autoLoad = setInterval(() => {
      dispatch(
        setTankStatisticFilter({
          ...tankStatisticFilter,
          endTime: new Date().getTime(),
        })
      )
      dispatch(loadTankStatistic())
    }, 10000)
    return () => clearInterval(autoLoad)
  })

  React.useEffect(() => {
    setFilter(tankStatisticFilter)
  }, [tankStatisticFilter])

  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const columns = React.useMemo(
    () => [
      {
        title: translate('tankStatistic.id', language),
        render: (_: any, tankStatistic: TankStatisticEntity) =>
          tankStatistic.tank.id,
        width: 100,
      },
      {
        title: translate('tankStatistic.tank', language),
        render: (_: any, tankStatistic: TankStatisticEntity) =>
          tankStatistic.tank.name,
        width: 180,
      },
      {
        title: translate('tankStatistic.station', language),
        render: (_: any, tankStatistic: TankStatisticEntity) =>
          tankStatistic.tank.station.name,
        width: 180,
      },
      {
        title: translate('tankStatistic.fuel', language),
        render: (_: any, tankStatistic: TankStatisticEntity) =>
          tankStatistic.tank.fuel.name,
        width: 180,
      },
      {
        title: translate('tankStatistic.volume', language),
        render: (_: any, tankStatistic: TankStatisticEntity) => (
          <NumberFormat
            value={tankStatistic.tank.volume}
            suffixKey="common.unitType"
          />
        ),
        width: 120,
      },
      {
        title: translate('tankStatistic.remain', language),
        render: (_: any, tankStatistic: TankStatisticEntity) => (
          <NumberFormat
            value={tankStatistic.tank.remain}
            suffixKey="common.unitType"
          />
        ),
        width: 120,
      },
      {
        title: translate('tankStatistic.import', language),
        render: (_: any, tankStatistic: TankStatisticEntity) => (
          <NumberFormat
            value={tankStatistic.totalImport}
            suffixKey="common.unitType"
          />
        ),
        width: 120,
      },
      {
        title: translate('tankStatistic.export', language),
        render: (_: any, tankStatistic: TankStatisticEntity) => (
          <NumberFormat
            value={tankStatistic.totalExport}
            suffixKey="common.unitType"
          />
        ),
        width: 120,
      },
    ],
    [language]
  )

  return (
    <TankStatisticContainer style={{ isDesktopScreen }}>
      <TankStatisticTitle>
        {translate('tankStatistic.title', language)}
      </TankStatisticTitle>
      <StatisticFilterComponent
        onSearch={() => {
          dispatch(setTankStatisticFilter(filter))
          dispatch(setTankStatisticLoaded(false))
          dispatch(loadTankStatistic())
        }}
        rangeValue={{ startTime: filter.startTime, endTime: filter.endTime }}
        selectedStationIds={filter.stationIds}
        onRangePickerChange={(e) => {
          if (e && e.length === 2) {
            setFilter({
              ...filter,
              startTime: e && e[0] ? e[0].toDate().getTime() : filter.startTime,
              endTime: e && e[1] ? e[1].toDate().getTime() : filter.endTime,
            })
          }
        }}
        onSelectStationChange={(e) => {
          setFilter({
            ...filter,
            stationIds: e,
          })
        }}
      />
      <TableComponent
        data={tankStatistic}
        columns={columns}
        loading={!isTankStatisticLoaded}
      />
    </TankStatisticContainer>
  )
}
